/**
 * triggerContract.ts — turn a user-TYPED trigger into a real contract.
 *
 * A typed trigger ("Powell resigns") is just text today: a placeholder marker with
 * no written rule for what counts as it happening and no deadline. So nothing can
 * grade it, which means a branch built on one can never have its premise settled.
 *
 * This module is the pure half of fixing that: it composes what gets sent to the
 * SAME preprocessor a typed forecast question goes through, and it validates what
 * comes back against the parent. No React, no fetch.
 *
 * ── Why the parent has to be in the prompt ──────────────────────────────────
 * Preprocessing "Powell resigns" ALONE gives the writer no domain, no timeframe,
 * and — the load-bearing one — no upper bound. A trigger's window must close on or
 * before its parent's resolution date, because a trigger that resolves after the
 * question it was supposed to inform cannot inform it. Machine-generated triggers
 * are held to exactly that rule by a deterministic gate
 * (`trigger_gen.py::_contract_gate`, which keeps a future event only if
 * `end <= parent_resolution_date`). A hand-typed one should be held to the same
 * standard rather than a laxer one.
 *
 * Giving the preprocessor the parent question and its close date gets coherent
 * criteria and an in-bounds deadline for free; `validateTriggerContract` then
 * checks rather than trusts.
 *
 * ── Always the BINARY preprocessor ──────────────────────────────────────────
 * A premise is yes/no — "did this happen by D?" — even when the parent forecast is
 * multi-valued. Callers must use `/preprocess`, never `/preprocess/auto`, which
 * would classify some typed events as categorical or numeric and hand back a
 * contract the premise comparison cannot grade at all (see `branchPremise.ts`,
 * whose verdict vocabulary is yes/no/tie/unresolved).
 */

import { parseResolveDateLocal, resolutionMomentLocal } from "../formatDates.js";
import { dayInZone, exactDeadlineOf } from "./forecastClient.js";
import { calendarDay } from "./calendarDay.js";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The parent contract fields this module reads. */
export interface ParentContractLike {
  question?: string | null;
  title?: string | null;
  resolution_date?: string | null;
}

/** What the preprocessor returns (the fields that matter here). */
export interface PreprocessedQuestionLike {
  question_text?: string | null;
  resolution_criteria?: string | null;
  scheduled_resolution_time?: string | null;
  background?: string | null;
  /** Where the contract server rides its present-tense `assertion` (see below) and the
   *  resolved deadline (`resolution_moment_utc` + `resolution_zone`). Open-ended because it
   *  is a general bag — `prediction_date` rides here too. */
  custom_metadata?:
    | ({
        assertion?: string | null;
        resolution_moment_utc?: string | null;
        resolution_zone?: string | null;
      } & Record<string, unknown>)
    | null;
}

/** A typed trigger's contract, in the shape the forecast server writes it from. */
export interface TriggerContract {
  question: string;
  resolution_criteria: string;
  resolution_date: string;
  background: string;
  /** The claim as a PRESENT-TENSE statement, for surfaces that state it rather than ask it.
   *  A typed trigger is an assertion — rendering its contract QUESTION reads as
   *  'If "Will X?" happens …'. Empty when the server did not produce one, and callers then
   *  fall back to `question`. */
  assertion: string;
}

/** Why a returned contract was rejected, or `null` when it is usable. */
export type TriggerContractProblem =
  | "no_question"
  | "no_criteria"
  | "no_date"
  | "bad_date"
  | "after_parent"
  | "in_past";

/**
 * Compose the text handed to the preprocessor for a typed trigger.
 *
 * Deliberately a single composed STRING rather than structured fields: the
 * contract server's `PreprocessRequest` carries only `question_text`, so framing is
 * something the caller writes into that one field or does not get at all.
 *
 * The parent's close date is included when known, because it is the bound the
 * result is then checked against — asking for a contract without telling the writer
 * the deadline it must respect, and then rejecting it for missing the deadline,
 * would be a needlessly high failure rate.
 *
 * `asOf` (a YYYY-MM-DD day string) tells the writer what "now" is, so a relative
 * phrase in the user's text ("next month") resolves against the timeline the user
 * is looking at rather than the server's clock.
 */
export function composeTriggerPrompt(args: {
  typed: string;
  parent?: ParentContractLike | null;
  forkDate?: string | null;
  asOf?: string | null;
}): string {
  const typed = (args.typed || "").trim();
  const parentQ = args.parent?.question || args.parent?.title || "";
  const parentClose = args.parent?.resolution_date || "";
  const lines: string[] = [];
  lines.push(
    `Author a self-contained YES/NO contract for this event: "${typed}".`,
    // "Self-contained" is the same word the machine-trigger schema uses, and it
    // matters: the question must not refer back to the parent by pronoun, because
    // it becomes a standalone contract that gets graded on its own.
    `The question must resolve YES if and only if the event occurs, and must stand alone (no pronouns or references back to any other question).`,
  );
  if (parentQ) {
    // Context only — explicitly NOT the thing being authored. Without this
    // instruction the writer sometimes authors a contract for the PARENT question
    // instead of the trigger, which is a silent and confusing failure.
    lines.push(
      `For context only, this event is being used as a trigger for a separate forecast: "${parentQ}". Do NOT author a contract for that question — author one for the event above.`,
    );
  }
  if (args.forkDate) lines.push(`The user placed the event on ${args.forkDate}.`);
  if (parentClose) {
    lines.push(
      `The event's resolution date MUST be on or before ${parentClose}, the date the separate forecast resolves.`,
    );
  }
  if (args.asOf) lines.push(`Today is ${args.asOf}; resolve any relative dates against it.`);
  return lines.join(" ");
}

/**
 * Check a preprocessed trigger against its parent, returning the mintable contract
 * or the reason it cannot be used.
 *
 * Checks rather than trusts the framing: a prompt is a request, not a guarantee,
 * and a trigger whose window closes after its parent's resolution — or in the past
 * — is worse than no trigger, because it mints a contract that can never usefully
 * grade the branch built on it.
 *
 * `after_parent` is a REJECTION, not a clamp. Silently moving the date would keep
 * the question's wording ("...by March 2027") while grading it on a different date,
 * which is a contract whose text and behaviour disagree.
 */
export function validateTriggerContract(args: {
  preprocessed?: PreprocessedQuestionLike | null;
  parent?: ParentContractLike | null;
  nowMs?: number;
}): { ok: true; contract: TriggerContract } | { ok: false; problem: TriggerContractProblem } {
  const p = args.preprocessed || null;
  const question = (p?.question_text || "").trim();
  if (!question) return { ok: false, problem: "no_question" };
  const criteria = (p?.resolution_criteria || "").trim();
  // A trigger with no criteria is exactly the thing this whole module exists to
  // avoid minting — it would look gradable and never be.
  if (!criteria) return { ok: false, problem: "no_criteria" };
  const rawDate = p?.scheduled_resolution_time || "";
  // The instant the server resolved, when it sent one. `scheduled_resolution_time` carries
  // the same instant as NAIVE UTC, which `new Date` reads as local, and a day-boundary
  // deadline is the asker's END of day — the next UTC day west of Greenwich. So the day is
  // the instant's day in its own zone, the rule `contractFromPreprocessed` applies to a typed
  // question.
  const exact = exactDeadlineOf(p);
  if (!rawDate && !exact) return { ok: false, problem: "no_date" };
  let day: string;
  if (exact) {
    day = dayInZone(exact, p?.custom_metadata?.resolution_zone || "");
  } else {
    const d = parseResolveDateLocal(rawDate);
    if (!d || isNaN(d.getTime())) return { ok: false, problem: "bad_date" };
    day = rawDate.slice(0, 10);
  }
  // A contract closes on a calendar day, and the mint refuses anything else: a close that is
  // not a real `YYYY-MM-DD` (2026-02-30, or a zone that rendered no day) is rejected here,
  // where the user can still fix it, rather than dropped there.
  if (!calendarDay(day)) return { ok: false, problem: "bad_date" };

  // Day against day, as the server's mint gate compares (`date_window_ok`): a trigger that
  // closes at any moment of the parent's close day can still inform it.
  const parentDay = (args.parent?.resolution_date || "").slice(0, 10);
  if (ISO_DAY.test(parentDay) && day > parentDay) {
    return { ok: false, problem: "after_parent" };
  }
  // A trigger already past its own deadline can still be graded (that is the
  // window-closed case premise grading handles), but it cannot be a FORWARD-looking
  // branch premise, and minting one would create a contract that resolves immediately
  // on a date the user has not reached.
  //
  // Compared against the END of the close day, not its midnight. A day-granular close
  // is open THROUGH that whole day — the rule `premiseWindow` and the server's own mint
  // gate both use — and `parseResolveDateLocal` gives its local MIDNIGHT, so comparing
  // that against the current instant rejected every contract closing TODAY. That is not
  // an edge case: the preprocessor is told the trigger must close on or before the
  // parent's resolution date, so a forecast resolving today or tomorrow could produce
  // nothing else. Short-horizon forecasts could therefore never carry a gradable
  // hand-placed trigger — the user was told "that event's deadline has already passed"
  // about a trigger whose deadline was hours away, and the branch it made was
  // permanently ungradable.
  //
  // A resolved instant is already that end; only a bare payload needs the day rule.
  const close = exact ?? resolutionMomentLocal(rawDate) ?? parseResolveDateLocal(rawDate);
  const nowMs = typeof args.nowMs === "number" ? args.nowMs : Date.now();
  if (close && !isNaN(close.getTime()) && close.getTime() < nowMs) {
    return { ok: false, problem: "in_past" };
  }

  return {
    ok: true,
    contract: {
      question,
      resolution_criteria: criteria,
      // Day-granular, matching what the forecast server writes and what the
      // contract hash canonicalizes.
      resolution_date: day,
      // `background` is deliberately excluded from the contract hash (same question
      // with different background is the same contract), so it can ride along
      // without destabilising identity.
      background: (p?.background || "").trim(),
      assertion: (p?.custom_metadata?.assertion || "").trim(),
    },
  };
}

/** Why the preprocess REQUEST failed, as opposed to what it returned: an expired session
 *  and a rate limit each need a different action from the user than an outage does. */
export type TriggerRequestProblem = "request_failed" | "unauthorized" | "rate_limited";

/** The request problem a non-ok preprocess response stands for. */
function requestProblemOf(status: number | undefined): TriggerRequestProblem {
  if (status === 401 || status === 403) return "unauthorized";
  if (status === 429) return "rate_limited";
  return "request_failed";
}

/** Reader-facing explanation for a rejection — shown when a typed trigger can't be
 *  made into a contract, so the user can fix it rather than seeing it fail mutely. */
export function explainTriggerProblem(
  problem: TriggerContractProblem | TriggerRequestProblem,
  parent?: ParentContractLike | null,
): string {
  const close = parent?.resolution_date || "the forecast's resolution date";
  switch (problem) {
    case "request_failed":
      return "Couldn't reach the service that turns that into a checkable event, so no branch was created. Try again in a moment.";
    case "unauthorized":
      return "Your session has expired, so no branch was created. Sign in again, then retry.";
    case "rate_limited":
      return "Too many requests right now, so no branch was created. Wait a moment, then retry.";
    case "no_question":
      return "Couldn't turn that into a checkable event — try naming what happens, and to whom.";
    case "no_criteria":
      return "Couldn't work out how that event would be judged. Try adding what would count as it happening.";
    case "no_date":
    case "bad_date":
      return "Couldn't work out when that event would be decided. Try adding a date or deadline.";
    case "after_parent":
      return `That event resolves after ${close}, when this forecast resolves — so it couldn't affect the outcome. Try an earlier deadline.`;
    case "in_past":
      return "That event's deadline has already passed, so it can't be used as a future trigger.";
  }
}

// ── The network hop ──────────────────────────────────────────────────────────

/**
 * Preprocess a user-TYPED trigger into a real contract, via the same endpoint a
 * typed forecast question goes through.
 *
 * `/api/forecast/preprocess` — the BINARY one, deliberately, never
 * `/preprocess/auto`. See the module header: a premise is yes/no even under a
 * multi-valued parent, and auto would classify some typed events as categorical or
 * numeric and return a contract the premise comparison cannot grade.
 *
 * Returns the validated contract, or a `problem` a caller can show the user and let
 * them fix — a typed trigger that cannot be made checkable should say so, not fail
 * mutely and mint a placeholder that never grades.
 *
 * `preprocessed` is returned alongside on success so a confirm/edit surface can show
 * the user exactly what came back before anything is minted. A rejected contract
 * carries the deadline it came back with (`close`), for telemetry.
 */
export async function preprocessTypedTrigger(args: {
  typed: string;
  parent?: ParentContractLike | null;
  forkDate?: string | null;
  asOf?: string | null;
  nowMs?: number;
  signal?: AbortSignal | null;
  /** Injected for tests; defaults to the shared apiPost. */
  post?: (
    url: string,
    body: unknown,
    options?: { signal?: AbortSignal | null; returnUnauthorized?: boolean },
  ) => Promise<{ ok: boolean; status: number; body: unknown }>;
}): Promise<
  | { ok: true; contract: TriggerContract; preprocessed: PreprocessedQuestionLike }
  | { ok: false; problem: TriggerContractProblem | TriggerRequestProblem; close?: string | null }
> {
  const post = args.post || (await import("../apiClient.js")).apiPost;
  const question_text = composeTriggerPrompt({
    typed: args.typed,
    parent: args.parent,
    forkDate: args.forkDate,
    asOf: args.asOf,
  });
  let res;
  try {
    // Same body shape the submit path sends: the route reads
    // `req.body.question.question_text` (server/proxy/forecast.js).
    // A 401 comes back rather than redirecting, so the typed text stays on screen with
    // "Sign in again" (requestProblemOf) instead of being lost to the sign-in redirect.
    res = await post(
      "/api/forecast/preprocess",
      { question: { question_text } },
      { signal: args.signal ?? null, returnUnauthorized: true },
    );
  } catch {
    return { ok: false, problem: "request_failed" };
  }
  if (!res || !res.ok) return { ok: false, problem: requestProblemOf(res?.status) };
  if (!res.body || typeof res.body !== "object") return { ok: false, problem: "request_failed" };
  const preprocessed = res.body as PreprocessedQuestionLike;
  const v = validateTriggerContract({
    preprocessed,
    parent: args.parent,
    nowMs: args.nowMs,
  });
  if (!v.ok) {
    const close =
      preprocessed.custom_metadata?.resolution_moment_utc ||
      preprocessed.scheduled_resolution_time ||
      null;
    return { ok: false, problem: v.problem, close };
  }
  return { ok: true, contract: v.contract, preprocessed };
}
